import { useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api, till } from "@/lib/api";
import { money, moneyShort, personName } from "@/lib/format";
import { Button, Input, Pager, Select, cx, usePaged } from "./ui";
import { lastPaidOf, useSuppliers } from "./SupplierBits";
import { useShop, useTill } from "@/lib/till-context";
import { Card, PageHead, td, th } from "./Layout";

type Sum = { sales: number; count: number; average: number; cost: number; profit: number; margin: number | null; discounts: number };
type Product = { id: string; name: string; unit: string | null; category: string; qty: number; sales: number; cost: number | null; profit: number | null; margin: number | null; onHand: number | null; lastsDays: number | null };
type Owed = { id: string; name: string; phone: string; limit: number | null; terms: number; owes: number; d30: number; d60: number; d90: number; over: number; oldestDays: number; overdue: number };
export type Insights = {
  period: { from: number; to: number; days: number; monthly: boolean };
  now: Sum;
  before: Sum;
  trend: { key: string; amount: number }[];
  methods: Record<string, number>;
  wholesale: number;
  products: Product[];
  owed: { total: number; overdue: number; overdueCustomers: number; buckets: { d30: number; d60: number; d90: number; over: number }; customers: Owed[] };
  stock: {
    valueCost: number; valueRetail: number; noCost: number; units: number; products: number; low: number; out: number;
    reorder: { id: string; name: string; unit: string | null; onHand: number; perWeek: number; lastsDays: number | null; suggest: number; pack: { name: string; count: number } | null }[];
    slow: { id: string; name: string; unit: string | null; onHand: number; tiedUp: number | null }[];
  };
  staff: { id: string; name: string; sales: number; count: number; discounts: number; discountCount: number; priceChanged: number; refunds: number; refundCount: number; overShort: number; hours: number }[];
  yard?: { today: number; waiting: number; out: number; done: number; collect: number };
  quotes?: { made: number; madeTotal: number; converted: number; convertedTotal: number; open: number; rate: number | null };
  needs: { overdueAccounts: number; overdueAmount: number; reorder: number; quotesExpiring: number; quotesExpiringTotal: number; deliveriesNotOut: number };
};

export const PERIODS: [string, string, number][] = [
  ["today", "Today", 0],
  ["7", "7 days", 6],
  ["28", "28 days", 27],
  ["90", "90 days", 89],
  ["year", "This year", -1],
];
export function periodRange(p: string) {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const row = PERIODS.find((x) => x[0] === p) ?? PERIODS[2]!;
  if (row[2] === -1) start.setMonth(0, 1);
  else start.setDate(start.getDate() - row[2]);
  return { from: start.getTime(), to: Date.now() };
}

export function useInsights(period: string, shop = "") {
  return useQuery({
    queryKey: ["insights", period, shop],
    queryFn: () => {
      const { from, to } = periodRange(period);
      return api<Insights>(till(`/admin/insights?from=${from}&to=${to}&shop=${encodeURIComponent(shop)}`));
    },
    refetchInterval: 120_000,
  });
}

const units = (n: number, unit: string | null) => `${n.toLocaleString("en-GB")}${unit ? ` ${unit}${n === 1 || unit.length <= 2 || /s$/.test(unit) ? "" : /(x|ch|sh)$/.test(unit) ? "es" : "s"}` : ""}`;
const label = (key: string) => {
  const [y, m, dd] = key.split("-").map(Number);
  return new Date(y!, (m ?? 1) - 1, dd ?? 1).toLocaleDateString("en-GB", dd ? { day: "numeric", month: "short" } : { month: "short", year: "numeric" });
};
const METHOD: Record<string, string> = { momo: "Mobile Money", card: "Card", cash: "Cash", credit: "Store credit", account: "On credit", transfer: "Bank transfer", cheque: "Cheque" };

function Change({ now, before, unit = "%" }: { now: number; before: number; unit?: string }) {
  if (!before) return <span className="text-xs text-slate-500">nothing before to compare</span>;
  const pct = Math.round(((now - before) / before) * 100);
  return <span className={cx("text-xs font-bold", pct >= 0 ? "text-[#1d6b40]" : "text-[#a3261b]")}>{pct >= 0 ? "▲" : "▼"} {Math.abs(pct)}{unit} vs before</span>;
}

/** A small period switch shared by the insight pages. */
export function PeriodSwitch({ value, onChange }: { value: string; onChange: (p: string) => void }) {
  return (
    <div role="radiogroup" aria-label="Period" className="ui-period flex flex-wrap gap-1 bg-slate-100 p-1">
      {PERIODS.map(([k, l]) => (
        <button key={k} type="button" role="radio" aria-checked={value === k} onClick={() => onChange(k)} className={cx("h-9 px-3 text-[13px] font-semibold", value === k ? "bg-white shadow-sm" : "text-slate-600")}>
          {l}
        </button>
      ))}
    </div>
  );
}

/** Spreadsheet download (opens in Excel). */
export function downloadCsv(name: string, rows: (string | number | null)[][]) {
  const esc = (v: string | number | null) => (v == null ? "" : /[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v));
  const blob = new Blob(["﻿" + rows.map((r) => r.map(esc).join(",")).join("\r\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${name}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

/** The top of the dashboard: headline numbers against the period before, the trend, how people paid, top products, needs you. */
export function Overview({ d }: { d: Insights }) {
  const shop = useShop();
  // Delivery switched off: nothing about the yard or deliveries shows.
  const deliveryOn = !!useTill().me?.settings.delivery?.enabled;
  const { now, before } = d;
  const max = Math.max(1, ...d.trend.map((t) => t.amount));
  const best = d.trend.reduce((a, b) => (b.amount > a.amount ? b : a), d.trend[0] ?? { key: "", amount: 0 });
  const paid = Object.entries(d.methods).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
  const paidTotal = Math.max(1, paid.reduce((n, [, v]) => n + v, 0));
  const needs = [
    d.needs.overdueAccounts ? { n: d.needs.overdueAccounts, t: `Account${d.needs.overdueAccounts === 1 ? "" : "s"} overdue · ${moneyShort(d.needs.overdueAmount)}`, to: "/owed", cta: "Remind", red: true } : null,
    d.needs.reorder ? { n: d.needs.reorder, t: "Products to order soon", to: "/health", cta: "See list", red: false } : null,
    d.needs.quotesExpiring ? { n: d.needs.quotesExpiring, t: `Quotes expire this week · ${moneyShort(d.needs.quotesExpiringTotal)}`, to: "/reports", cta: "Follow up", red: false } : null,
    deliveryOn && d.needs.deliveriesNotOut ? { n: d.needs.deliveriesNotOut, t: "Deliveries due, not yet out", to: "/", cta: "Ask the yard", red: false } : null,
  ].filter(Boolean) as { n: number; t: string; to: string; cta: string; red: boolean }[];
  const tiles: [string, string, React.ReactNode][] = [
    ["Sales", moneyShort(now.sales), <Change key="s" now={now.sales} before={before.sales} />],
    ["Profit", now.margin == null ? "No cost prices" : moneyShort(now.profit), now.margin == null ? <span key="p" className="text-xs text-slate-500">Add cost prices to products</span> : <span key="p" className="text-xs text-slate-600"><b>{now.margin}%</b> of sales{before.margin != null ? ` · was ${before.margin}%` : ""}</span>],
    ["Sales count", now.count.toLocaleString("en-GB"), <Change key="c" now={now.count} before={before.count} />],
    ["Average sale", moneyShort(now.average), <Change key="a" now={now.average} before={before.average} />],
    ["Owed to you", moneyShort(d.owed.total), d.owed.overdue ? <span key="o" className="text-xs font-bold text-[#a3261b]">{moneyShort(d.owed.overdue)} overdue</span> : <span key="o" className="text-xs text-slate-500">none overdue</span>],
    ["Stock value (cost)", moneyShort(d.stock.valueCost), <span key="v" className="text-xs text-slate-500">{d.stock.products} products{d.stock.noCost ? ` · ${d.stock.noCost} without a cost` : ""}</span>],
  ];
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 2xl:grid-cols-6">
        {tiles.map(([l, v, s]) => (
          <div key={l} className="flex flex-col gap-1 border border-slate-200 bg-white p-4">
            <span className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">{l}</span>
            <span className="font-mono text-2xl font-bold">{v}</span>
            {s}
          </div>
        ))}
      </div>
      <div className="grid gap-4 [&>*]:min-w-0 xl:grid-cols-[1.7fr_1fr]">
        <Card title={d.period.monthly ? "Sales by month" : "Sales by day"} right={best.amount ? <span className="text-xs text-slate-500">Best: {label(best.key)} · {moneyShort(best.amount)}</span> : undefined}>
          <div className="px-5 pb-4">
            <div className="flex h-44 items-end gap-[3px] border-b border-slate-300" role="img" aria-label={`Sales over the period, best ${label(best.key)} at ${money(best.amount)}`}>
              {d.trend.map((t) => (
                <span key={t.key} title={`${label(t.key)} · ${money(t.amount)}`} className="flex-1 rounded-t-[3px] bg-brand" style={{ height: `${Math.max(t.amount ? 2 : 0, (t.amount / max) * 100)}%` }} />
              ))}
            </div>
            <div className="mt-1 flex justify-between font-mono text-[11px] text-slate-500">
              <span>{d.trend[0] ? label(d.trend[0].key) : ""}</span>
              <span>{d.trend.length ? label(d.trend[d.trend.length - 1]!.key) : ""}</span>
            </div>
          </div>
        </Card>
        <Card title="How customers paid">
          <div className="flex flex-col gap-2.5 px-5 pb-5 text-sm">
            {paid.length ? (
              paid.map(([m, v]) => (
                <div key={m} className="grid grid-cols-[110px_1fr_auto] items-center gap-3">
                  <span>{METHOD[m] ?? m}</span>
                  <span className="h-2.5 overflow-hidden bg-slate-100">
                    <i className="block h-full bg-brand" style={{ width: `${(v / paidTotal) * 100}%` }} />
                  </span>
                  <b className="font-mono text-[13px]">{Math.round((v / paidTotal) * 100)}% · {moneyShort(v)}</b>
                </div>
              ))
            ) : (
              <span className="text-slate-500">No sales in this period.</span>
            )}
            {now.sales ? <span className="mt-1 text-xs text-slate-500">Retail {Math.round(((now.sales - d.wholesale) / now.sales) * 100)}% · wholesale {Math.round((d.wholesale / now.sales) * 100)}% of sales</span> : null}
          </div>
        </Card>
      </div>
      {d.yard && d.quotes && (!shop.serials || d.yard.today || d.quotes.made) ? (
        <div className="grid gap-4 [&>*]:min-w-0 lg:grid-cols-2">
          {deliveryOn ? <Card title="The yard today" right={<span className="text-xs text-slate-500">{d.yard.collect} collection{d.yard.collect === 1 ? "" : "s"}, {d.yard.today - d.yard.collect} deliver{d.yard.today - d.yard.collect === 1 ? "y" : "ies"}</span>}>
            <div className="grid grid-cols-3 gap-3 px-5 pb-5">
              {([["Waiting or loading", d.yard.waiting, d.yard.waiting ? "text-amber-800" : ""], ["On the road", d.yard.out, ""], ["Delivered or collected", d.yard.done, d.yard.done ? "text-[#1d6b40]" : ""]] as const).map(([l, v, c]) => (
                <div key={l} className="border border-slate-200 px-3 py-2.5">
                  <span className="text-xs text-slate-500">{l}</span>
                  <div className={cx("font-mono text-2xl font-bold", c)}>{v}</div>
                </div>
              ))}
            </div>
          </Card> : null}
          <Card title="Quotes" right={<Link to="/reports" className="text-[13px] font-semibold text-brand">Reports</Link>}>
            <div className="grid grid-cols-3 gap-3 px-5 pb-5">
              <div className="border border-slate-200 px-3 py-2.5"><span className="text-xs text-slate-500">Made</span><div className="font-mono text-2xl font-bold">{d.quotes.made}</div><span className="text-[11px] text-slate-500">{moneyShort(d.quotes.madeTotal)}</span></div>
              <div className="border border-slate-200 px-3 py-2.5"><span className="text-xs text-slate-500">Became sales</span><div className="font-mono text-2xl font-bold">{d.quotes.converted}</div><span className="text-[11px] text-slate-500">{d.quotes.rate == null ? "-" : `${d.quotes.rate}% · ${moneyShort(d.quotes.convertedTotal)}`}</span></div>
              <div className="border border-slate-200 px-3 py-2.5"><span className="text-xs text-slate-500">Still open</span><div className="font-mono text-2xl font-bold">{d.quotes.open}</div><span className="text-[11px] text-slate-500">follow these up</span></div>
            </div>
          </Card>
        </div>
      ) : null}
      <div className="grid gap-4 [&>*]:min-w-0 xl:grid-cols-[1.7fr_1fr]">
        <Card title="Top products" right={<Link to="/reports" className="text-[13px] font-semibold text-brand">Profit by product</Link>}>
          <div className="flex flex-col px-5 pb-4 text-sm">
            {d.products.slice(0, 6).map((p) => (
              <div key={p.id} className="grid grid-cols-[minmax(0,1fr)_auto_auto] sm:grid-cols-[minmax(0,1fr)_150px_110px] gap-3 border-t border-slate-100 py-2">
                <span className="truncate">{p.name}</span>
                <span className="font-mono text-slate-600">{units(p.qty, p.unit)}</span>
                <b className="text-right font-mono">{moneyShort(p.sales)}</b>
              </div>
            ))}
            {!d.products.length ? <span className="text-slate-500">No sales in this period.</span> : null}
          </div>
        </Card>
        <Card title="Follow up">
          <div className="flex flex-col gap-2 px-5 pb-5 text-sm">
            {needs.length ? (
              needs.map((x) => (
                <div key={x.t} className="flex items-center gap-3 border border-slate-200 px-3 py-2.5">
                  <b className={cx("grid h-8 w-8 flex-none place-items-center font-mono", x.red ? "bg-red-50 text-red-800" : "bg-amber-50 text-amber-900")}>{x.n}</b>
                  <span className="flex-1">{x.t}</span>
                  <Link to={x.to} className="font-semibold text-brand">
                    {x.cta}
                  </Link>
                </div>
              ))
            ) : (
              <span className="text-slate-500">Nothing waiting on you.</span>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

/** Profit by product for the period, with a spreadsheet download. */
export function ProfitByProduct() {
  const [p, setP] = useState("28");
  const q = useInsights(p);
  const [find, setFind] = useState("");
  const d = q.data;
  const rows = (d?.products ?? []).filter((x) => !find.trim() || x.name.toLowerCase().includes(find.trim().toLowerCase()));
  const paged = usePaged(rows);
  return (
    <Card
      title="Profit by product"
      right={
        <div className="flex flex-wrap items-center gap-2">
          <PeriodSwitch value={p} onChange={setP} />
          <button
            type="button"
            disabled={!d}
            onClick={() => d && downloadCsv(`profit-by-product-${p}`, [["Product", "Sold", "Unit", "Sales", "Cost", "Profit", "Margin %", "In stock", "Lasts (days)"], ...d.products.map((x) => [x.name, x.qty, x.unit, x.sales / 100, x.cost == null ? null : x.cost / 100, x.profit == null ? null : x.profit / 100, x.margin, x.onHand, x.lastsDays])])}
            className="h-9 border border-slate-300 bg-white px-3 text-[13px] font-semibold"
          >
            Excel
          </button>
          <button type="button" onClick={() => window.print()} className="h-9 border border-slate-300 bg-white px-3 text-[13px] font-semibold">
            PDF
          </button>
        </div>
      }
    >
      {d ? (
        <div className="flex flex-col">
          <div className="grid grid-cols-2 gap-3 px-5 pb-3 sm:grid-cols-4">
            {[["Sales", d.now.sales], ["Cost of items sold", d.now.cost], ["Profit", d.now.profit]].map(([l, v]) => (
              <div key={l as string} className="border border-slate-200 px-3 py-2"><span className="text-xs text-slate-500">{l}</span><div className="font-mono text-lg font-bold">{money(v as number)}</div></div>
            ))}
            <div className="border border-slate-200 px-3 py-2"><span className="text-xs text-slate-500">Profit %</span><div className="font-mono text-lg font-bold">{d.now.margin == null ? "-" : `${d.now.margin}%`}</div></div>
          </div>
          <div className="px-5 pb-3">
            <input value={find} onChange={(e) => setFind(e.target.value)} placeholder="Find a product" aria-label="Find a product" className="h-10 w-full max-w-xs border border-slate-300 px-3 text-sm" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                  {["Product", "Sold", "Sales", "Cost", "Profit", "Margin", "In stock", "Lasts"].map((h, i) => (
                    <th key={h} className={cx("whitespace-nowrap px-5 py-2 font-semibold", i > 0 && "text-right")}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paged.rows.map((x) => (
                  <tr key={x.id} className="border-t border-slate-100">
                    <td className="px-5 py-2.5 font-semibold">{x.name}</td>
                    <td className="whitespace-nowrap px-5 py-2.5 text-right font-mono">{units(x.qty, x.unit)}</td>
                    <td className="whitespace-nowrap px-5 py-2.5 text-right font-mono">{money(x.sales)}</td>
                    <td className="whitespace-nowrap px-5 py-2.5 text-right font-mono">{x.cost == null ? <span className="text-slate-400">no cost</span> : money(x.cost)}</td>
                    <td className="whitespace-nowrap px-5 py-2.5 text-right font-mono">{x.profit == null ? "-" : money(x.profit)}</td>
                    <td className="whitespace-nowrap px-5 py-2.5 text-right font-mono">{x.margin == null ? "-" : `${x.margin}%`}</td>
                    <td className="whitespace-nowrap px-5 py-2.5 text-right font-mono">{x.onHand == null ? "-" : units(x.onHand, x.unit)}</td>
                    <td className={cx("whitespace-nowrap px-5 py-2.5 text-right font-mono", x.lastsDays != null && x.lastsDays < 7 && "font-bold text-[#a3261b]")}>{x.lastsDays == null ? "-" : x.lastsDays === 0 ? "Out" : `${x.lastsDays} days`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={paged.page} size={paged.size} total={paged.total} onPage={paged.setPage} />
          <span className="px-5 py-3 text-xs text-slate-500">Profit uses each product's cost price as set now. "Lasts" is stock left at this period's selling rate.</span>
        </div>
      ) : (
        <p className="px-5 pb-5 text-sm text-slate-500">Loading…</p>
      )}
    </Card>
  );
}

const wa = (phone: string, text: string) => `https://wa.me/${phone.replace(/^0/, "233")}?text=${encodeURIComponent(text)}`;

/** Money owed by customers on account, by how long it's been owed. */
export function OwedPage() {
  const q = useInsights("28");
  const d = q.data?.owed;
  const paged = usePaged(d?.customers ?? []);
  const b = d?.buckets;
  const total = Math.max(1, d?.total ?? 0);
  const shades = ["#f7c59f", "#ec8f4c", "var(--brand)", "#6b2c05"];
  return (
    <>
      <PageHead title="Money owed to you" sub={d ? `${d.customers.length} customer${d.customers.length === 1 ? "" : "s"} owe ${money(d.total)}` : "Loading…"}>
        {d ? <button type="button" onClick={() => downloadCsv("money-owed", [["Customer", "Phone", "Owes", "0-30", "31-60", "61-90", "90+", "Overdue", "Limit"], ...d.customers.map((c) => [c.name, c.phone, c.owes / 100, c.d30 / 100, c.d60 / 100, c.d90 / 100, c.over / 100, c.overdue / 100, c.limit == null ? null : c.limit / 100])])} className="h-10 border border-slate-300 bg-white px-3 text-sm font-semibold">Excel</button> : null}
      </PageHead>
      {d && b ? (
        <div className="flex flex-col gap-4">
          <Card title="By how long it's been owed">
            <div className="flex flex-col gap-3 px-5 pb-5">
              <div className="flex h-5 gap-[2px]" role="img" aria-label={`0 to 30 days ${money(b.d30)}, 31 to 60 ${money(b.d60)}, 61 to 90 ${money(b.d90)}, over 90 ${money(b.over)}`}>
                {[b.d30, b.d60, b.d90, b.over].map((v, i) => (v ? <span key={i} style={{ flex: v, background: shades[i] }} /> : null))}
              </div>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                {(["0 to 30 days", "31 to 60 days", "61 to 90 days", "Over 90 days"] as const).map((l, i) => (
                  <div key={l} className="border border-slate-200 px-3 py-2">
                    <span className="flex items-center gap-2 text-xs text-slate-600"><i className="block h-3 w-3" style={{ background: shades[i] }} />{l}</span>
                    <b className="font-mono text-lg">{money([b.d30, b.d60, b.d90, b.over][i]!)}</b>
                    <span className="block text-[11px] text-slate-500">{Math.round(([b.d30, b.d60, b.d90, b.over][i]! / total) * 100)}%</span>
                  </div>
                ))}
              </div>
              {d.overdue ? <span className="text-sm font-bold text-[#a3261b]">{money(d.overdue)} is overdue, across {d.overdueCustomers} customer{d.overdueCustomers === 1 ? "" : "s"}</span> : <span className="text-sm text-slate-500">Nothing is overdue.</span>}
            </div>
          </Card>
          <Card title="Customers">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px] text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                    {["Customer", "Owes", "0-30", "31-60", "61-90", "90+", "Limit used", ""].map((h, i) => (
                      <th key={h || i} className={cx("whitespace-nowrap px-5 py-2 font-semibold", i > 0 && i < 7 && "text-right")}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paged.rows.map((c) => (
                    <tr key={c.id} className="border-t border-slate-100">
                      <td className="px-5 py-2.5"><b>{c.name}</b><div className="text-xs text-slate-500">{c.phone} · {c.terms} days</div></td>
                      <td className="whitespace-nowrap px-5 py-2.5 text-right font-mono font-bold">{money(c.owes)}</td>
                      {[c.d30, c.d60, c.d90, c.over].map((v, i) => (
                        <td key={i} className={cx("whitespace-nowrap px-5 py-2.5 text-right font-mono", v > 0 && i > 0 && "font-bold text-[#a3261b]")}>{v ? money(v) : "-"}</td>
                      ))}
                      <td className="whitespace-nowrap px-5 py-2.5 text-right font-mono">{c.limit ? `${Math.round((c.owes / c.limit) * 100)}% of ${moneyShort(c.limit)}` : "-"}</td>
                      <td className="px-5 py-2.5 text-right">
                        <a href={wa(c.phone, c.overdue ? `Hello ${c.name}, a reminder that ${money(c.overdue)} on your account is past the ${c.terms}-day terms. Total owed: ${money(c.owes)}. Thank you.` : `Hello ${c.name}, your account statement: ${money(c.owes)} owed. Thank you.`)} target="_blank" rel="noreferrer" className="inline-flex h-9 items-center border border-slate-300 px-3 text-[13px] font-semibold">
                          {c.overdue ? "WhatsApp reminder" : "Statement"}
                        </a>
                      </td>
                    </tr>
                  ))}
                  {!d.customers.length ? <tr><td colSpan={8} className="px-5 py-5 text-slate-500">Nobody owes anything.</td></tr> : null}
                </tbody>
              </table>
            </div>
            <Pager page={paged.page} size={paged.size} total={paged.total} onPage={paged.setPage} />
          </Card>
        </div>
      ) : (
        <p className="text-sm text-slate-500">{q.isError ? "Couldn't load" : "Loading…"}</p>
      )}
    </>
  );
}

/** Stock value, what to order soon (two weeks of sales, in whole packs), and what isn't moving. */
export function StockHealthPage() {
  const [shop, setShop] = useState("");
  const [tab, setTab] = useState<"order" | "slow">("order");
  const [find, setFind] = useState("");
  const shopsQ = useQuery({ queryKey: ["shops"], queryFn: () => api<{ shops: { id: string; name: string }[] }>(till("/admin/shops")) });
  const shops = shopsQ.data?.shops ?? [];
  const q = useInsights("28", shop);
  const s = q.data?.stock;
  const sup = useSuppliers();
  const itemsQ = useQuery({ queryKey: ["admin-items"], queryFn: () => api<{ items: { id: string; mainSupplierId?: string | null; backupSupplierId?: string | null }[] }>(till("/admin/items")), staleTime: 60_000, enabled: !!import.meta.env.VITE_BRAND });
  const whoToCall = (id: string) => {
    const it = itemsQ.data?.items.find((x) => x.id === id);
    const one = (label: string, sid?: string | null) => {
      const name = sup.data?.suppliers.find((x) => x.id === sid)?.name;
      if (!name) return null;
      const paid = lastPaidOf(sup.data, id, sid);
      return `${label}: ${name}${paid != null ? ` ${moneyShort(paid)}` : ""}`;
    };
    return [one("Main", it?.mainSupplierId), one("Backup", it?.backupSupplierId)].filter(Boolean).join(" · ");
  };
  const words = find.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const matches = (name: string) => words.every((w) => name.toLowerCase().includes(w));
  const order = (s?.reorder ?? []).filter((r) => matches(r.name));
  const slow = (s?.slow ?? []).filter((x) => matches(x.name));
  const orderPage = usePaged(order);
  const slowPage = usePaged(slow);
  const tiedUp = (s?.slow ?? []).reduce((n, x) => n + (x.tiedUp ?? 0), 0);
  const urgent = (r: { onHand: number; lastsDays: number | null }) => r.onHand <= 0 || (r.lastsDays ?? 99) <= 7;
  const exportList = () => {
    if (!s) return;
    if (tab === "order") downloadCsv("order-soon", [["Product", "In stock", "Sells a week", "Lasts (days)", "Suggest ordering", "Unit"], ...s.reorder.map((r) => [r.name, r.onHand, r.perWeek, r.lastsDays, r.suggest, r.unit])]);
    else downloadCsv("not-selling", [["Product", "In stock", "Unit", "Money tied up"], ...s.slow.map((x) => [x.name, x.onHand, x.unit, x.tiedUp == null ? null : x.tiedUp / 100])]);
  };
  const tabs = [["order", "Order soon", s?.reorder.length ?? 0], ["slow", "Not selling", s?.slow.length ?? 0]] as const;
  return (
    <>
      <PageHead title="Stock health" sub="What to order, and what isn't selling, based on the last 28 days.">
        {shops.length > 1 ? <Select aria-label="Shop" value={shop} onChange={(e) => setShop(e.target.value)} className="!w-52"><option value="">All shops</option>{shops.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</Select> : null}
        <Button disabled={!s} onClick={exportList}>Download Excel</Button>
      </PageHead>
      {s ? (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
            {([["Stock value", moneyShort(s.valueCost), `At cost · ${moneyShort(s.valueRetail)} at selling prices`], ["Running low", String(s.low), s.low === 1 ? "1 product at its low level" : "Products at their low level"], ["Out of stock", String(s.out), s.out ? "Can't be sold right now" : "Everything is available"], ["Money tied up", moneyShort(tiedUp), `${s.slow.length} product${s.slow.length === 1 ? "" : "s"} not selling`]] as const).map(([l, v, n]) => (
              <div key={l} className="min-w-0 border border-slate-200 bg-white p-4"><span className="text-xs font-semibold text-slate-500">{l}</span><div className="mt-1 truncate text-[26px] font-extrabold leading-tight tracking-tight">{v}</div><div className="mt-0.5 truncate text-xs text-slate-500">{n}</div></div>
            ))}
          </div>
          <Card>
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-3 pt-1.5">
              <div role="tablist" aria-label="Stock health" className="no-scrollbar flex overflow-x-auto">
                {tabs.map(([k, l, n]) => (
                  <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} className={cx("flex h-11 items-center gap-2 whitespace-nowrap !rounded-none border-b-2 px-3.5 text-sm", tab === k ? "border-brand font-bold" : "border-transparent text-slate-500")}>
                    {l}{n ? <span className={cx("ui-pill px-2 text-[11px] font-extrabold", tab === k ? "bg-brand text-white" : "bg-slate-100 text-slate-600")}>{n}</span> : null}
                  </button>
                ))}
              </div>
              <Input aria-label="Find a product" value={find} onChange={(e) => setFind(e.target.value)} placeholder="Find a product" className="mb-1.5 !h-10 !w-60 max-w-full" />
            </div>
            {tab === "order" ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead><tr>{["Product", "In stock", "Runs out in", "Order", ""].map((h, i) => <th key={i} className={cx(th, (i === 1 || i >= 3) && "text-right")}>{h || <span className="sr-only">Actions</span>}</th>)}</tr></thead>
                    <tbody>
                      {orderPage.rows.map((r) => (
                        <tr key={r.id}>
                          <td className={cx(td, "whitespace-normal")}><b className="block font-semibold">{r.name}</b><span className="text-xs text-slate-500">Sells {r.perWeek} a week{whoToCall(r.id) ? ` · ${whoToCall(r.id)}` : ""}</span></td>
                          <td className={cx(td, "text-right")}>{units(r.onHand, r.unit)}</td>
                          <td className={td}><span className={cx("ui-pill inline-block whitespace-nowrap px-2 py-0.5 text-xs font-bold", urgent(r) ? "bg-warn-tint text-warn-ink" : "bg-slate-100 text-slate-700")}>{r.onHand <= 0 ? "Already out" : r.lastsDays == null ? "Not sure" : `${r.lastsDays} day${r.lastsDays === 1 ? "" : "s"}`}</span></td>
                          <td className={cx(td, "text-right font-bold")}>{units(r.suggest, r.unit)}{r.pack ? <span className="block text-xs font-normal text-slate-500">{r.pack.count} {r.pack.name}{r.pack.count === 1 ? "" : "s"}</span> : null}</td>
                          <td className={cx(td, "text-right")}><Link to={`/stock?item=${encodeURIComponent(r.id)}`} className="text-[13px] font-semibold text-brand hover:underline">Receive</Link></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!order.length ? <p className="px-5 py-10 text-center text-sm text-slate-500">{find ? "No product matches." : "Nothing needs ordering yet."}</p> : null}
                <Pager page={orderPage.page} size={orderPage.size} total={orderPage.total} onPage={orderPage.setPage} />
                <p className="px-5 py-3 text-xs text-slate-500">The amount to order covers two weeks of sales plus the low stock level, rounded up to whole packs.</p>
              </>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead><tr>{["Product", "In stock", "Money tied up"].map((h, i) => <th key={h} className={cx(th, i > 0 && "text-right")}>{h}</th>)}</tr></thead>
                    <tbody>
                      {slowPage.rows.map((x) => (
                        <tr key={x.id}>
                          <td className={cx(td, "whitespace-normal font-semibold")}>{x.name}</td>
                          <td className={cx(td, "text-right")}>{units(x.onHand, x.unit)}</td>
                          <td className={cx(td, "text-right font-bold")}>{x.tiedUp == null ? <span className="font-normal text-slate-500">No cost price</span> : moneyShort(x.tiedUp)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!slow.length ? <p className="px-5 py-10 text-center text-sm text-slate-500">{find ? "No product matches." : "Everything in stock sold in the last 60 days."}</p> : null}
                <Pager page={slowPage.page} size={slowPage.size} total={slowPage.total} onPage={slowPage.setPage} />
                {s.slow.length ? <p className="px-5 py-3 text-[13px] text-slate-500">No sale in 60 days. You could <Link to="/discounts" className="font-semibold text-brand hover:underline">start a discount</Link>, sell them with a best seller, or return them to the supplier.</p> : null}
              </>
            )}
          </Card>
        </div>
      ) : (
        <p className="text-sm text-slate-500">{q.isError ? "Couldn't load stock health" : "Loading…"}</p>
      )}
    </>
  );
}

/** What each person rang up, discounts and refunds they gave, cash short or over, hours on shift. */
export function StaffPerformance() {
  const [p, setP] = useState("28");
  const q = useInsights(p);
  const rows = q.data?.staff ?? [];
  const paged = usePaged(rows);
  const max = Math.max(1, ...rows.map((r) => r.sales));
  return (
    <Card title="How each person is doing" right={<PeriodSwitch value={p} onChange={setP} />}>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[780px] border-collapse">
          <thead><tr>{["Person", "Total sales", "No. of sales", "Average", "Discounts", "Refunds", "Cash difference", "Hours"].map((h, i) => <th key={h} className={cx(th, i > 0 && "text-right")}>{h}</th>)}</tr></thead>
          <tbody>
            {paged.rows.map((r) => (
              <tr key={r.id}>
                <td className={cx(td, "font-semibold")}>{personName(r.name)}{r.priceChanged ? <span className="block text-xs font-normal text-slate-500">{r.priceChanged} price change{r.priceChanged === 1 ? "" : "s"}</span> : null}</td>
                <td className={cx(td, "text-right font-bold")}><span className="inline-flex items-center justify-end gap-2"><span className="h-2 w-16 rounded bg-slate-100"><span className="block h-2 rounded bg-brand" style={{ width: `${(r.sales / max) * 100}%` }} /></span>{moneyShort(r.sales)}</span></td>
                <td className={cx(td, "text-right")}>{r.count}</td>
                <td className={cx(td, "text-right")}>{r.count ? moneyShort(Math.round(r.sales / r.count)) : "-"}</td>
                <td className={cx(td, "text-right")}>{r.discountCount ? moneyShort(r.discounts) : "-"}</td>
                <td className={cx(td, "text-right")}>{r.refundCount ? moneyShort(r.refunds) : "-"}</td>
                <td className={cx(td, "text-right", r.overShort < 0 && "font-semibold text-warn-ink")}>{r.overShort ? `${r.overShort < 0 ? "Short" : "Over"} ${moneyShort(Math.abs(r.overShort))}` : "Exact"}</td>
                <td className={cx(td, "text-right")}>{r.hours ? Math.round(r.hours) : "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length ? <p className="px-5 py-6 text-center text-sm text-slate-500">{q.isLoading ? "Loading…" : "Nothing in this period."}</p> : null}
      </div>
      <Pager page={paged.page} size={paged.size} total={paged.total} onPage={paged.setPage} />
    </Card>
  );
}
